import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { defineMessages } from 'react-intl';
import OkCancelModal from '@/shared/components/OkCancelModal';
import api from '@/shared/api';
import config from '@/config';
import log from 'loglevel';
import intlShape from '@/shared/utils/intlShape';
import ToastrService from '@/shared/services/ToastrService';
import EmailEditor from '@/shared/components/EmailEditor';
import { injectStateService } from '@/shared/services/StateService';
import handleHttpError from '@/shared/services/HandleHttpError';
import moment from 'moment';
import RelationSearch from '@/shared/components/input/RelationSearch';
import { mapSuggestedMailAddress } from '@/shared/services/ObjectDisplayService';
import LoadingAnimation from '../LoadingAnimation';
import {
  combineForRequest,
  createEmptyRecipients,
  createRecipientsFromCommunicationSettings,
  getCommunicationSettings,
  getEmailRecipients,
  Purpose,
  sanitizeRecipients,
  validateRecipients,
} from './emailHelpers';
import EmailRecipients from './EmailRecipients';
import messages from './emailMessages';

class ForwardEmailModal extends Component {
  state = {
    email: undefined,
    tags: [],
    recipients: createEmptyRecipients(),
    emailAttachments: [],
    selectedRelation: undefined,
  };

  componentDidMount() {
    this.form = React.createRef();
    this.initializeModal();
  }

  static propTypes = {
    intl: intlShape.isRequired,
    relationId: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    show: PropTypes.bool,
    documentId: PropTypes.string.isRequired,
  };

  static defaultProps = {
    relationId: null,
    show: false,
  };

  initializeModal = () => {
    const { documentId, relationId, invoiceStreamId, intl } = this.props;
    const emptyPromise = () => Promise.resolve();

    const emailContentPromise = api.base.get(
      `${config.appUrls.documents}/api/emails/ForwardInfo/${documentId}`,
    );

    const communicationSettingsPromise = getCommunicationSettings(relationId, Purpose.Other);

    const emailAddressesPromise = getEmailRecipients(relationId, null, intl);

    this.setState({ loading: true });
    Promise.all([emailContentPromise, emailAddressesPromise, communicationSettingsPromise])
      .then(([{ data: forwardInfo }, { addresses, employees }, { communicationSettings }]) => {
        this.setState(({ recipients }) => ({
          loading: false,
          addresses,
          employees,
          recipients: sanitizeRecipients({
            ...recipients,
            ...createRecipientsFromCommunicationSettings(communicationSettings, addresses),
          }),
          companyId: forwardInfo.companyId,
          tags: forwardInfo.tags,
          email: {
            title: forwardInfo.subject,
            initialValue: forwardInfo.content,
            content: forwardInfo.content,
            documentDate: moment.utc(),
          },
          emailAttachments: forwardInfo.attachments || [],
          selectedRelation: relationId ? { key: relationId } : undefined,
        }));
      })
      .catch(err => {
        handleHttpError(intl)(err);
        this.setState({ loading: false });
      });
  };

  validateEmail = value => {
    if (!value) return false;
    return /^[^@]+@.+\..+$/.test(value);
  };

  onSave = () => {
    const {
      email,
      recipients,
      emailAttachments,
      selectedTemplate,
      companyId,
      selectedRelation,
      tags,
    } = this.state;
    const { intl } = this.props;
    const isFormValid = this.validate();

    if (!validateRecipients(recipients)) {
      ToastrService.error(intl.formatMessage(messages.msgInvalidEmailAddress));
      return false;
    }

    let invalidEmailBody = false;

    if (
      !email.content ||
      // regex to remove line breaks
      email.content.replace(/(\r\n|\n|\r)/gm, '') === '<p></p>'
    ) {
      invalidEmailBody = true;
    }

    if (invalidEmailBody || !isFormValid) {
      ToastrService.error(intl.formatMessage(messages.SubjectBodyRecipientsAreRequired));
      return Promise.reject(new Error('invalid form'));
    }

    const emailViewModel = {
      ...combineForRequest(recipients),
      body: email.content,
      subject: email.title,
      attachmentDocumentIds: emailAttachments.map(x => x.id) || [],
      relationId: selectedRelation && selectedRelation.key,
      companyId: selectedRelation?.companyId ?? companyId, // prioritize newly selected relation's company over company that was set on the original email
      emailLinkType: 4,
      documentDate: email.documentDate,
      emailTemplateId: selectedTemplate,
      tags,
    };

    return api.base
      .post(`${config.appUrls.documents}/api/emails/CreateAndSend`, emailViewModel)
      .then(() => {
        ToastrService.info(intl.formatMessage(messages.EmailSent));
        this.closeModal();
      })
      .catch(handleHttpError(intl));
  };

  closeModal = () => {
    const { onClose } = this.props;
    onClose();
  };

  emailTitleChanged = title => {
    const { email } = this.state;

    this.setState({
      email: {
        ...email,
        title,
      },
    });
  };

  onEditorStateChange = content => {
    const { email } = this.state;
    this.setState({
      email: {
        ...email,
        content,
      },
    });
  };

  renderSingleEmail = email => {
    const documentUploadEndpoint = `${config.appUrls.documents}/api/documents?documentType=9&informQueues=false&recognitionStatus=4`;
    const { emailAttachments } = this.state;
    const { relationId } = this.props;
    return (
      <EmailEditor
        title={email.title}
        onTitleChanged={title => this.emailTitleChanged(title)}
        initialValue={email.initialValue}
        onContentChanged={content => this.onEditorStateChange(content)}
        onAttachmentUploaded={(error, file) => this.onDocumentUploaded(error, file)}
        attachmentUploadEndpoint={documentUploadEndpoint}
        attachments={emailAttachments}
        onDeleteAttachment={this.deleteAttachment}
        initialRelation={relationId}
        documentDate={email.documentDate}
        onDocumentDateChanged={documentDate =>
          this.setState({
            email: {
              ...email,
              documentDate,
            },
          })
        }
      />
    );
  };

  onDocumentUploaded = (error, document) => {
    const { intl } = this.props;

    if (error) {
      log.error(error);
      ToastrService.error(intl.formatMessage(messages.MsgEmailAttachmentUploadFailed));
      return;
    }

    const { emailAttachments } = this.state;

    if (emailAttachments.some(x => x.id === document.id)) return;

    this.setState(state => ({
      ...state,
      emailAttachments: [document, ...state.emailAttachments],
    }));
  };

  // when deleting an attachment when forwarding an email we wont delete the original attachment from our system but rather not send it with the new email
  deleteAttachment = attachment => {
    this.setState(state => ({
      emailAttachments: state.emailAttachments.filter(x => x.id !== attachment.id),
    }));
  };

  validate() {
    const { email } = this.state;
    if (!email) return false;
    if (!this.form || !this.form.current) return false;

    return this.form.current.reportValidity();
  }

  relationChanged = item => {
    if (!item || !item.id) {
      this.setState({ selectedRelation: null }, () => this.getAddressesForRelation());
    } else {
      this.setState({ selectedRelation: item }, () => this.getAddressesForRelation());
    }
  };

  getAddressesForRelation() {
    const { intl } = this.props;
    const { selectedRelation } = this.state;
    const relationId = selectedRelation?.key;

    const communicationSettingsPromise = getCommunicationSettings(relationId, Purpose.Other);
    const emailAddressesPromise = getEmailRecipients(relationId, null, intl);

    Promise.all([emailAddressesPromise, communicationSettingsPromise])
      .then(([{ addresses, employees }, { communicationSettings }]) => {
        this.setState(({ recipients }) => ({
          addresses,
          employees,
          recipients: sanitizeRecipients({
            ...recipients,
            ...createRecipientsFromCommunicationSettings(communicationSettings, addresses),
          }),
        }));
      })
      .catch(err => {
        handleHttpError(intl)(err);
        this.setState({ loading: false });
      });
  }
  handleRecipientChange = (type, newValues) => {
    this.setState(({ recipients }) => ({
      recipients: {
        ...recipients,
        [type]: newValues || [],
      },
    }));
  };

  render() {
    const { show, intl } = this.props;
    const { email, addresses, loading, selectedRelation, recipients, employees } = this.state;
    return (
      <OkCancelModal
        title={intl.formatMessage(messages.Forward)}
        show={show}
        onClose={this.closeModal}
        onSave={this.onSave}
        size="lg"
      >
        {show && (
          <form ref={this.form}>
            <div className="form-horizontal">
              {loading && <LoadingAnimation />}
              {email && (
                <React.Fragment>
                  <div className="form-group">
                    <label className="control-label col-sm-3">
                      {intl.formatMessage(messages.Relation)}
                    </label>
                    <div className="col-md-6">
                      <RelationSearch
                        placeholder={intl.formatMessage(messages.SelectRelationToRetrieveAddresses)}
                        onChange={this.relationChanged}
                        initialSelectedItem={selectedRelation}
                      />
                    </div>
                  </div>
                  <EmailRecipients
                    value={recipients}
                    addresses={addresses}
                    employees={employees}
                    onChange={this.handleRecipientChange}
                  />
                </React.Fragment>
              )}

              {email && this.renderSingleEmail(email)}
            </div>
          </form>
        )}
      </OkCancelModal>
    );
  }
}
export default injectStateService(ForwardEmailModal);
